/**
 * A camada SOCIAL do escritório 2D: o que só existe no mapa quando há PESSOAS
 * nele — os balões de fala por cima das cabeças e o círculo no chão de uma
 * conversa de corredor.
 *
 * Vive à parte do `officeArt` (que desenha o CENÁRIO, estático e imutável) e à
 * parte do `OfficeSpace` (que é o loop) por uma razão prática: são coisas
 * que aparecem e desaparecem a toda a hora, e cada uma precisa de uma POOL —
 * criar e destruir objectos do Phaser a 60fps é a forma mais fácil de engasgar
 * uma cena. Cada camada aqui guarda o que já criou, reaproveita-o enquanto o
 * dono continuar em cena, e só destrói quando ele sai de vez.
 *
 * Todas as camadas seguem o mesmo contrato: um `render(items, now)` chamado uma
 * vez por frame com a lista COMPLETA do que deve estar visível. Quem chama não
 * gere criação nem destruição — dá o retrato de agora e a camada trata do resto.
 * É o mesmo padrão do `reconcileRemotes`, e pela mesma razão: um estado
 * desenhado a partir de um retrato nunca fica dessincronizado do estado real.
 *
 * O `Phaser` vem por PARÂMETRO onde é preciso, como em `drawWorld`. Tem de vir:
 * os tipos declaram um `namespace Phaser` GLOBAL, por isso `Phaser.Math` passa o
 * typecheck em qualquer sítio — mas o bundle ESM que o Vite carrega nunca põe
 * nada em `window`. Usá-lo sem o receber compila, constrói, e só rebenta em
 * runtime com um `ReferenceError` a meio do frame.
 */

import { SAY_TTL_MS, TILE } from "@greenvoice/shared";
import type PhaserT from "phaser";

/** Acima de tudo o que o mundo desenha (o y-sort vai até ~`ROWS * TILE`). */
const DEPTH_SAY = 30_000;
/** Letreiros presos ao cenário: alto, mas por baixo dos balões de fala. */
const DEPTH_BADGE = 29_000;
/**
 * O círculo da conversa é CHÃO: acima das etiquetas de sala (-6000) e abaixo de
 * qualquer corpo ou móvel (que usam y-sort ≥ 0). Fica debaixo dos pés de quem
 * está a conversar, que é onde uma marca no chão tem de ficar.
 */
const DEPTH_RING = -5_000;

// ── balões de fala ──────────────────────────────────────────────────────────

/** Largura máxima do balão antes de partir a linha. */
const SAY_WRAP = 168;
const SAY_PAD_X = 9;
const SAY_PAD_Y = 6;
/** Altura do bico que aponta para a cabeça. */
const SAY_TAIL = 7;
/** Quanto o balão sobe acima dos pés — o nome do avatar está a -54. */
const SAY_LIFT = 70;
/** O balão não desaparece de repente: apaga-se nos últimos instantes. */
const SAY_FADE_MS = 800;

export interface SayItem {
  /** O dono do balão (o id do utilizador) — é a chave da pool. */
  id: string;
  /** Posição dos PÉS do avatar, em coordenadas do mundo. */
  x: number;
  y: number;
  text: string;
  /** Epoch ms de quando foi dito — daqui sai a idade e o TTL. */
  at: number;
}

interface SayEntry {
  container: PhaserT.GameObjects.Container;
  bg: PhaserT.GameObjects.Graphics;
  label: PhaserT.GameObjects.Text;
  /** O texto já desenhado — evita remedir e repintar o fundo a cada frame. */
  drawn: string;
}

export interface SayLayer {
  render(items: readonly SayItem[], now: number): void;
}

/**
 * Desenha o fundo do balão à volta do texto já medido: um rectângulo redondo com
 * um bico a apontar para baixo, ancorado com a BASE no zero do container (por
 * isso todos os `y` são negativos). Ancorar por baixo é o que faz um balão de
 * duas linhas crescer para CIMA em vez de descer para dentro da cabeça.
 */
function drawSayBubble(bg: PhaserT.GameObjects.Graphics, w: number, h: number): void {
  const boxW = w + SAY_PAD_X * 2;
  const boxH = h + SAY_PAD_Y * 2;
  const top = -boxH - SAY_TAIL;
  bg.clear();
  bg.fillStyle(0x0e141c, 0.92);
  bg.lineStyle(1, 0x2b3a4d, 1);
  bg.fillRoundedRect(-boxW / 2, top, boxW, boxH, 7);
  bg.strokeRoundedRect(-boxW / 2, top, boxW, boxH, 7);
  // O bico por cima da moldura, sem contorno na base: a costura entre o bico e a
  // caixa ficaria a ver-se como um risco por dentro do balão.
  bg.fillTriangle(-5, -SAY_TAIL - 0.5, 5, -SAY_TAIL - 0.5, 0, 0);
}

/** A pool dos balões de fala — um por pessoa, o mais recente substitui o anterior. */
export function createSayLayer(scene: PhaserT.Scene): SayLayer {
  const pool = new Map<string, SayEntry>();
  const alive = new Set<string>();

  return {
    render(items, now) {
      alive.clear();
      for (const item of items) {
        const age = now - item.at;
        if (age >= SAY_TTL_MS) continue; // já passou: deixa-se cair na limpeza
        alive.add(item.id);

        let entry = pool.get(item.id);
        if (!entry) {
          const bg = scene.add.graphics();
          const label = scene.add
            .text(0, 0, "", {
              fontFamily: "Inter, system-ui, sans-serif",
              fontSize: "11px",
              color: "#e8eef6",
              align: "center",
              wordWrap: { width: SAY_WRAP },
            })
            .setOrigin(0.5, 1)
            .setResolution(2);
          const container = scene.add.container(item.x, item.y, [bg, label]).setDepth(DEPTH_SAY);
          entry = { container, bg, label, drawn: "" };
          pool.set(item.id, entry);
        }

        if (entry.drawn !== item.text) {
          entry.label.setText(item.text);
          entry.label.setY(-SAY_TAIL - SAY_PAD_Y);
          drawSayBubble(entry.bg, entry.label.width, entry.label.height);
          entry.drawn = item.text;
        }

        const fading = age - (SAY_TTL_MS - SAY_FADE_MS);
        entry.container.setPosition(item.x, item.y - SAY_LIFT);
        entry.container.setAlpha(fading > 0 ? 1 - fading / SAY_FADE_MS : 1);
      }

      for (const [id, entry] of pool) {
        if (alive.has(id)) continue;
        entry.container.destroy(true);
        pool.delete(id);
      }
    },
  };
}

// ── círculos de conversa (huddle de corredor) ───────────────────────────────

/** Margem do anel para lá da pessoa mais afastada do centro. */
const RING_PAD = TILE * 0.9;
/** O chão é visto de cima mas em perspectiva: o anel é uma elipse, não um círculo. */
const RING_SQUASH = 0.52;
/** Período do pulsar, em ms — devagar, é uma respiração, não um alarme. */
const RING_PULSE_MS = 1_800;

export interface HuddleRing {
  /** O id do grupo (a âncora) — chave da pool e do token do LiveKit. */
  id: string;
  /** Centro do grupo, em coordenadas do mundo. */
  x: number;
  y: number;
  /** Distância do centro à pessoa mais afastada. */
  spread: number;
  /** Estou eu neste grupo? Muda a cor: o meu é verde, os dos outros são frios. */
  mine: boolean;
}

interface RingEntry {
  ellipse: PhaserT.GameObjects.Ellipse;
  mine: boolean;
}

export interface HuddleLayer {
  render(rings: readonly HuddleRing[], now: number): void;
}

/**
 * Os anéis no chão que mostram uma conversa a acontecer fora de qualquer sala.
 *
 * A cor diz tudo o que é preciso saber de longe: verde = estás nesta conversa,
 * azul frio = está ali uma conversa de outras pessoas. Não há botão nem convite
 * — aproximas-te e entras, afastas-te e sais, como num corredor a sério.
 */
export function createHuddleLayer(scene: PhaserT.Scene, Phaser: typeof PhaserT): HuddleLayer {
  const pool = new Map<string, RingEntry>();
  const alive = new Set<string>();

  return {
    render(rings, now) {
      alive.clear();
      // Uma só fase da onda para todos os anéis: dois círculos a pulsar
      // desalinhados dariam um efeito de discoteca em vez de calma.
      const pulse = Math.sin((now / RING_PULSE_MS) * Math.PI * 2);
      for (const ring of rings) {
        alive.add(ring.id);
        const d = (ring.spread + RING_PAD) * 2;
        let entry = pool.get(ring.id);
        if (!entry) {
          const ellipse = scene.add.ellipse(ring.x, ring.y, d, d * RING_SQUASH).setDepth(DEPTH_RING);
          ellipse.setBlendMode(Phaser.BlendModes.ADD);
          entry = { ellipse, mine: !ring.mine }; // força o primeiro `setStrokeStyle`
          pool.set(ring.id, entry);
        }
        if (entry.mine !== ring.mine) {
          entry.mine = ring.mine;
          const colour = ring.mine ? 0x39d353 : 0x5aa6e8;
          entry.ellipse.setFillStyle(colour, 0.07);
          entry.ellipse.setStrokeStyle(2, colour, 0.55);
        }
        entry.ellipse.setPosition(ring.x, ring.y);
        entry.ellipse.setSize(d, d * RING_SQUASH);
        entry.ellipse.setScale(1 + pulse * 0.02);
        entry.ellipse.setAlpha(0.75 + pulse * 0.25);
      }

      for (const [id, entry] of pool) {
        if (alive.has(id)) continue;
        entry.ellipse.destroy();
        pool.delete(id);
      }
    },
  };
}

// ── a televisão da sala de descanso ─────────────────────────────────────────

/** O letreiro fica acima do topo da TV, não à frente do ecrã. */
const TV_LIFT = 52;

export interface TvBadge {
  render(text: string | null, now: number): void;
}

/**
 * O letreiro "AO VIVO" por cima da televisão.
 *
 * Serve uma coisa só, e é a razão de a TV existir: quem atravessa a sala de
 * descanso VÊ que há uma apresentação a acontecer, sem abrir menu nenhum e sem
 * ninguém ter de o anunciar. É o equivalente à porta de vidro de uma sala de
 * reuniões — a informação está no sítio por onde se passa.
 *
 * O ponto vermelho pulsa devagar (1,4s) porque é isso que o olho lê como
 * "está a acontecer AGORA"; um letreiro parado lê-se como decoração.
 */
export function createTvBadge(scene: PhaserT.Scene, x: number, y: number): TvBadge {
  let container: PhaserT.GameObjects.Container | null = null;
  let bg: PhaserT.GameObjects.Graphics | null = null;
  let dot: PhaserT.GameObjects.Graphics | null = null;
  let label: PhaserT.GameObjects.Text | null = null;
  let drawn = "";

  return {
    render(text, now) {
      if (text === null) {
        // Ninguém a apresentar: o letreiro sai de cena por inteiro. Deixá-lo
        // invisível guardava um Text e um Graphics por sala para nada.
        if (container) {
          container.destroy(true);
          container = null;
          bg = null;
          dot = null;
          label = null;
          drawn = "";
        }
        return;
      }

      if (!container) {
        bg = scene.add.graphics();
        dot = scene.add.graphics();
        label = scene.add
          .text(0, -4, "", {
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "10px",
            fontStyle: "600",
            color: "#f4dede",
          })
          .setOrigin(0, 1)
          .setResolution(2);
        container = scene.add.container(x, y - TV_LIFT, [bg, dot, label]).setDepth(DEPTH_BADGE);
      }

      if (drawn !== text && bg && dot && label) {
        label.setText(text);
        const boxW = label.width + 26;
        const boxH = label.height + 8;
        // Origem do texto à esquerda, mas a caixa toda centrada na TV.
        label.setX(-boxW / 2 + 20);
        bg.clear();
        bg.fillStyle(0x1a0f12, 0.95);
        bg.lineStyle(1, 0x8c3b3b, 1);
        bg.fillRoundedRect(-boxW / 2, -boxH, boxW, boxH, 4);
        bg.strokeRoundedRect(-boxW / 2, -boxH, boxW, boxH, 4);
        dot.clear();
        dot.fillStyle(0xe05252, 1);
        dot.fillCircle(-boxW / 2 + 11, -boxH / 2, 3.5);
        drawn = text;
      }

      if (dot) dot.setAlpha(0.45 + 0.55 * (0.5 + 0.5 * Math.sin((now / 1400) * Math.PI * 2)));
    },
  };
}
